import type { ChangeEvent, FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';

import { useSection } from 'src/components/app/context/SectionContext';
import { useReportConfigAllocationCombinationQuery } from 'src/components/reportConfig/hooks/useReportConfigAllocationQuery';
import useReportConfigCombinations from 'src/components/reportConfig/hooks/useReportConfigCombinations';

import { styles } from './index.styles';

export const ADVANCED_SEARCH_TOGGLE_TEST_ID = 'advanced-search-toggle';
export const ADVANCED_SEARCH_CONTENT_TEST_ID = 'advanced-search-content';

export interface AdvancedSearchProps {
  advancedSearchFilters: any;
  handleAdvancedSearchFilterChange: any;
}

const AdvancedSearch: FC<AdvancedSearchProps> = ({
  advancedSearchFilters,
  handleAdvancedSearchFilterChange,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const { t } = useTranslation('report');
  const { moduleId } = useSection();
  const { data: reportConfigData, loading } =
    useReportConfigAllocationCombinationQuery(moduleId, !collapsed);
  const { configuredReportTypes, configuredFileTypes } =
    useReportConfigCombinations(reportConfigData);
  const handleCollapse = () => {
    setCollapsed((prevState) => !prevState);
  };

  const handleChange = (event: SelectChangeEvent, key: string) => {
    handleAdvancedSearchFilterChange(key, event.target.value);
  };

  return (
    <Box sx={styles.advancedSearch}>
      <Stack
        direction="row"
        onClick={handleCollapse}
        data-test-id={ADVANCED_SEARCH_TOGGLE_TEST_ID}
        role="button"
        sx={{ justifyContent: 'center', ...styles.button }}
      >
        {t('advancedSearch.advancedSearch')}
      </Stack>
      {!collapsed && (
        <Grid
          container
          spacing={1}
          data-test-id={ADVANCED_SEARCH_CONTENT_TEST_ID}
          sx={{ mt: 2, width: '100%' }}
        >
          <Grid
            item
            xs={3}
            sx={{
              alignContent: 'end',
            }}
          >
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <FormControl fullWidth>
                <Select
                  onChange={(e) => handleChange(e, 'report_type')}
                  value={advancedSearchFilters.report_type}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">
                    {t('advancedSearch.anyReportType')}
                  </MenuItem>

                  {configuredReportTypes.map(({ code, name }) => (
                    <MenuItem key={code} value={code}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>

          <Grid item xs={3} sx={{ alignContent: 'end' }}>
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <FormControl fullWidth>
                <Select
                  onChange={(e) => handleChange(e, 'file_type')}
                  value={advancedSearchFilters.file_type}
                  displayEmpty
                  size="small"
                >
                  <MenuItem value="">
                    {t('advancedSearch.anyFileType')}
                  </MenuItem>

                  {configuredFileTypes.map(({ code, abbreviation }) => (
                    <MenuItem key={code} value={code}>
                      {abbreviation?.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>

          <Grid item xs={3}>
            <InputLabel htmlFor="advanced-search-start-date">
              {t('advancedSearch.startDate')}
            </InputLabel>
            <OutlinedInput
              size="small"
              type="date"
              id="advanced-search-start-date"
              value={advancedSearchFilters.date_start}
              fullWidth
              onChange={
                ((e: ChangeEvent<HTMLInputElement>) =>
                  handleChange(e, 'date_start')) as (e: ChangeEvent) => void
              }
            />
          </Grid>

          <Grid item xs={3}>
            <InputLabel htmlFor="advanced-search-end-date">
              {t('advancedSearch.endDate')}
            </InputLabel>
            <OutlinedInput
              size="small"
              type="date"
              id="advanced-search-end-date"
              value={advancedSearchFilters.date_end}
              fullWidth
              onChange={
                ((e: ChangeEvent<HTMLInputElement>) =>
                  handleChange(e, 'date_end')) as (e: ChangeEvent) => void
              }
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default AdvancedSearch;
